import React, { useState, useMemo } from "react";
import { MagnifyingGlass, CaretLeft, CaretRight, Check, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../shared/Button";
import { Input } from "../shared/Input";

// ==========================================
// DIAGNÓSTICO INTELIGENTE
// Cada módulo tiene su propio set de preguntas reales (no genéricas).
// Las respuestas se compilan directo a líneas de presupuesto (trabajo +
// cantidad + los mismos checks que ya usa "Añadir trabajo": exterior,
// zona_humeda, pladur, con_sanitarios, contraste_alto, nivel_oxido, etc.)
// ==========================================

const MODULOS_DIAG = [
  { id: "suelos", emoji: "🏠", nombre: "Suelos y parquet" },
  { id: "pintura_paramentos", emoji: "🖌️", nombre: "Pintura de paredes/techos" },
  { id: "pintura_carpinteria", emoji: "🎨", nombre: "Pintura de carpintería" },
  { id: "ceramica", emoji: "🔲", nombre: "Cerámica y alicatados" },
  { id: "mamparas", emoji: "🚿", nombre: "Mamparas de baño" },
  { id: "cocinas", emoji: "🍳", nombre: "Cocina" },
  { id: "fontaneria", emoji: "🚰", nombre: "Fontanería" },
  { id: "electricidad", emoji: "💡", nombre: "Electricidad" },
  { id: "instalaciones", emoji: "🚪", nombre: "Puertas, ventanas y persianas" },
  { id: "cierres", emoji: "🔒", nombre: "Cerrajería" },
  { id: "mantenimiento", emoji: "🔧", nombre: "Mantenimiento" },
];

const buscar = (trabajos, nombre) => trabajos.find((t) => t.nombre === nombre);

// Arma una línea con los mismos campos que "Añadir trabajo" ya manda al
// crear un presupuesto normal (precio/subtotal en base a calidad estándar
// como punto de partida — el cliente puede ajustar cantidad/calidad
// después en el paso de revisión, que ya recalcula todo solo).
const linea = (trabajos, nombre, cantidad, extra = {}) => {
  const t = buscar(trabajos, nombre);
  const cant = Math.round((Number(cantidad) || 0) * 100) / 100;
  if (!t || !cant || cant <= 0) return null;
  const precioUnitario = t.precio_std ?? 0;
  return {
    trabajo_id: t.id,
    trabajo_nombre: t.nombre,
    cantidad: cant,
    unidad: t.unidad,
    precio_unitario: precioUnitario,
    subtotal: Math.round(precioUnitario * cant * 100) / 100,
    calidad: "std",
    incluye_material: true,
    precio_material: 0,
    tiempo_estimado: Math.round((t.tiempo_base_horas || 0.5) * cant * 100) / 100,
    ...extra,
  };
};

// ---------- SUELOS ----------
const suelosFlujo = {
  getPreguntas: (r) => {
    const qs = [
      { id: "tipo_suelo", texto: "¿Qué tipo de suelo querés poner?", tipo: "choice", opciones: [
        { label: "Laminado", value: "laminado" },
        { label: "Vinílico SPC/LVT", value: "vinilico" },
        { label: "Parquet multicapa", value: "parquet" },
      ]},
    ];
    if (r.tipo_suelo === "laminado") qs.push({ id: "resistencia_laminado", texto: "¿Qué resistencia necesitás?", tipo: "choice", opciones: [
      { label: "AC4 — uso normal de vivienda", value: "ac4" },
      { label: "AC5 — más resistente, tránsito alto", value: "ac5" },
    ]});
    if (r.tipo_suelo === "vinilico") qs.push({ id: "grosor_vinilico", texto: "¿Qué grosor preferís?", tipo: "choice", opciones: [
      { label: "4mm — estándar", value: "4mm" },
      { label: "5mm — más resistente", value: "5mm" },
    ]});
    qs.push({ id: "retirada", texto: "¿Hay suelo actual que retirar?", tipo: "choice", opciones: [
      { label: "No, está vacío o es obra nueva", value: "no" },
      { label: "Sí, es laminado o flotante", value: "laminado" },
      { label: "Sí, es parquet encolado", value: "parquet" },
      { label: "Sí, es vinílico/PVC", value: "vinilico" },
      { label: "Sí, es cerámica o gres", value: "ceramica" },
    ]});
    qs.push({ id: "m2", texto: "¿Cuántos m² tiene la superficie?", tipo: "number", unidad: "m²", placeholder: "Ej: 45" });
    qs.push({ id: "estado_soporte", texto: "¿Cómo está el suelo base (antes de poner el nuevo)?", tipo: "choice", opciones: [
      { label: "Nivelado y liso", value: "bueno" },
      { label: "Desnivel leve (1-3mm)", value: "leve" },
      { label: "Desnivel importante (3-10mm)", value: "grave" },
      { label: "Con humedad visible", value: "humedad" },
    ]});
    qs.push({ id: "rodapie", texto: "¿Necesitás rodapié nuevo?", tipo: "boolean" });
    if (r.rodapie) qs.push({ id: "ml_rodapie", texto: "¿Cuántos metros lineales de rodapié, aproximadamente?", tipo: "number", unidad: "ml", placeholder: "Ej: 28", hint: r.m2 ? `Estimado según los m²: ~${Math.round(r.m2 * 0.4)}ml` : "" });
    qs.push({ id: "perfil_transicion", texto: "¿Hay algún cambio de nivel/tipo de suelo donde necesites un perfil de transición?", tipo: "boolean" });
    return qs;
  },
  compilar: (r, trabajos) => {
    const lineas = [];
    const nombreRetirada = { laminado: "Retirada suelo laminado/flotante", parquet: "Retirada parquet encolado", vinilico: "Retirada vinílico/PVC", ceramica: "Retirada solado" }[r.retirada];
    if (nombreRetirada) lineas.push(linea(trabajos, nombreRetirada, r.m2));
    if (r.estado_soporte === "leve") lineas.push(linea(trabajos, "Nivelado autonivelante fino 1-3mm", r.m2));
    if (r.estado_soporte === "grave" || r.estado_soporte === "humedad") lineas.push(linea(trabajos, "Nivelado autonivelante estándar 3-10mm", r.m2));
    const nombreInstalacion =
      r.tipo_suelo === "laminado" ? (r.resistencia_laminado === "ac5" ? "Instalación laminado AC5 8-12mm" : "Instalación laminado AC4 7-8mm") :
      r.tipo_suelo === "vinilico" ? (r.grosor_vinilico === "5mm" ? "Instalación vinílico SPC clic 5mm" : "Instalación vinílico SPC clic 4mm") :
      "Instalación parquet multicapa";
    lineas.push(linea(trabajos, nombreInstalacion, r.m2));
    if (r.rodapie && r.ml_rodapie) lineas.push(linea(trabajos, "Rodapié MDF lacado 8cm", r.ml_rodapie));
    if (r.perfil_transicion) lineas.push(linea(trabajos, "Perfil transición aluminio", 1));
    return { lineas: lineas.filter(Boolean), respuestas_diagnostico: r.estado_soporte === "humedad" ? { estado_soporte: "Con humedad visible" } : {} };
  },
};

// ---------- PINTURA PAREDES/TECHOS ----------
const paramentosFlujo = {
  getPreguntas: (r) => {
    const qs = [
      { id: "que_pintar", texto: "¿Qué necesitás pintar?", tipo: "choice", opciones: [
        { label: "Solo paredes", value: "paredes" },
        { label: "Solo techo", value: "techo" },
        { label: "Paredes + techo", value: "ambos" },
        { label: "Fachada exterior", value: "fachada" },
      ]},
    ];
    if (r.que_pintar === "paredes" || r.que_pintar === "ambos") {
      qs.push({ id: "superficie_actual", texto: "¿Cómo está la pared actualmente?", tipo: "choice", opciones: [
        { label: "Lisa", value: "lisa" },
        { label: "Con gotelé (textura)", value: "gotele" },
      ]});
      {/* Antes esto preguntaba "¿cambiás de color o es el mismo tono?" y ataba
          1 mano al mismo tono y 2 manos al cambio de color — pero alguien
          puede querer el mismo color y aun así 2 manos (pared deslucida,
          quiere un acabado más parejo). Se pregunta directo lo que en
          realidad decide el trabajo: cuántas manos. */}
      qs.push({ id: "capas", texto: "¿Cuántas manos de pintura hacen falta?", tipo: "choice", opciones: [
        { label: "1 mano — mismo color, la pared está en buen estado", value: "1" },
        { label: "2 manos — cambio de color, pared nueva, o buscás un acabado más parejo", value: "2" },
      ]});
      qs.push({ id: "m2_paredes", texto: "¿Cuántos m² de pared, aproximadamente?", tipo: "number", unidad: "m²", placeholder: "Ej: 60" });
      qs.push({ id: "zona_humeda", texto: "¿Es un baño o cocina (zona húmeda)?", tipo: "boolean", ayuda: "Usa una pintura satinada especial antimoho, no la misma del resto de la casa" });
    }
    if (r.que_pintar === "techo" || r.que_pintar === "ambos") {
      qs.push({ id: "m2_techo", texto: "¿Cuántos m² de techo?", tipo: "number", unidad: "m²", placeholder: "Ej: 25" });
    }
    if (r.que_pintar === "fachada") {
      qs.push({ id: "m2_fachada", texto: "¿Cuántos m² de fachada?", tipo: "number", unidad: "m²", placeholder: "Ej: 80" });
    }
    qs.push({ id: "manchas", texto: "¿Hay manchas negras o de humedad en la pared?", tipo: "choice", opciones: [
      { label: "No hay manchas", value: "no" },
      { label: "Sí, cubren toda la pared", value: "todas" },
      { label: "Sí, pero solo en algunas zonas puntuales", value: "zonas" },
    ]});
    if (r.manchas === "zonas") qs.push({ id: "manchas_m2", texto: "¿Cuántos m² cubren esas manchas, aproximadamente?", tipo: "number", unidad: "m²", placeholder: "Ej: 3", ayuda: "Así compramos la pintura antihumedad justa, no de más" });
    qs.push({ id: "grietas", texto: "¿Hay grietas o fisuras que reparar?", tipo: "boolean" });
    if (r.grietas) qs.push({ id: "ml_grietas", texto: "¿Cuántos metros lineales de grietas, aproximadamente?", tipo: "number", unidad: "ml", placeholder: "Ej: 5" });
    return qs;
  },
  compilar: (r, trabajos) => {
    const lineas = [];
    const capas = r.capas === "1" ? "1 capa" : "2 capas";
    if (r.que_pintar === "paredes" || r.que_pintar === "ambos") {
      const nombre = r.superficie_actual === "gotele" ? `Pintura gotelé ${capas}` : `Pintura plástica lisa ${capas}`;
      lineas.push(linea(trabajos, nombre, r.m2_paredes, { zona_humeda: !!r.zona_humeda }));
    }
    if (r.que_pintar === "techo" || r.que_pintar === "ambos") {
      lineas.push(linea(trabajos, "Pintura techo liso 2 capas", r.m2_techo));
    }
    if (r.que_pintar === "fachada") {
      lineas.push(linea(trabajos, "Pintura fachada exterior", r.m2_fachada));
    }
    if (r.manchas === "todas") {
      const m2total = (Number(r.m2_paredes) || 0) + (Number(r.m2_techo) || 0);
      lineas.push(linea(trabajos, "Tratamiento antihumedad", m2total || 10));
    } else if (r.manchas === "zonas" && r.manchas_m2) {
      lineas.push(linea(trabajos, "Tratamiento antihumedad", r.manchas_m2));
    }
    if (r.grietas && r.ml_grietas) lineas.push(linea(trabajos, "Reparación grietas y fisuras", r.ml_grietas));
    return { lineas: lineas.filter(Boolean), respuestas_diagnostico: {} };
  },
};

// ---------- PINTURA CARPINTERÍA (se ramifica por elemento) ----------
const carpinteriaFlujo = {
  getPreguntas: (r) => {
    const qs = [
      { id: "elemento", texto: "¿Qué querés pintar?", tipo: "choice", opciones: [
        { label: "Puerta", value: "puerta" },
        { label: "Ventana de madera", value: "ventana" },
        { label: "Radiador", value: "radiador" },
        { label: "Reja o barandilla", value: "reja" },
        { label: "Mueble/armario", value: "mueble" },
        { label: "Pasamanos de escalera", value: "pasamanos" },
        { label: "Rodapié ya existente (repintar)", value: "rodapie" },
      ]},
    ];
    if (r.elemento === "puerta") {
      qs.push({ id: "material", texto: "¿De qué material es la puerta?", tipo: "choice", opciones: [
        { label: "Madera", value: "madera" }, { label: "Metal/hierro", value: "metal" }, { label: "Aluminio", value: "aluminio" },
      ]});
      if (r.material === "madera") {
        qs.push({ id: "que_hacer", texto: "¿Qué necesita?", tipo: "choice", opciones: [
          { label: "Está muy dañada, hay que decapar y volver a pintar de cero", value: "decapar" },
          { label: "Buen estado, solo lijar y volver a lacar", value: "lacar" },
          { label: "Barnizar transparente (no cambia color, se ve la madera)", value: "barnizar" },
        ]});
        if (r.que_hacer === "lacar" || r.que_hacer === "decapar") qs.push({ id: "molduras", texto: "¿Tiene molduras/relieves?", tipo: "boolean" });
      }
      if (r.material === "madera" ? (r.que_hacer === "lacar" || r.que_hacer === "decapar") : (r.material === "metal" || r.material === "aluminio")) {
        qs.push({ id: "color_actual", texto: "¿De qué color es ahora?", tipo: "choice", opciones: [{ label: "Claro", value: "claro" }, { label: "Oscuro", value: "oscuro" }] });
        qs.push({ id: "color_nuevo", texto: "¿A qué color la querés pintar?", tipo: "choice", opciones: [{ label: "Claro", value: "claro" }, { label: "Oscuro", value: "oscuro" }] });
      }
      if (r.material === "metal") qs.push({ id: "nivel_oxido", texto: "¿Tiene óxido?", tipo: "choice", opciones: [
        { label: "Nada o casi nada", value: "ninguno" }, { label: "Un poco", value: "poco" }, { label: "Bastante", value: "mucho" },
      ]});
      qs.push({ id: "tamano_especial", texto: "¿Es una puerta de tamaño estándar (0.90 x 2.10m) o especial?", tipo: "choice", opciones: [
        { label: "Estándar", value: "estandar" }, { label: "Especial, la mido yo", value: "especial" },
      ]});
      if (r.tamano_especial === "especial") qs.push({ id: "medidas", texto: "Medidas reales de la puerta", tipo: "ancho_alto" });
      qs.push({ id: "exterior", texto: "¿Da al exterior?", tipo: "boolean", ayuda: "Necesita una formulación distinta, resistente a la intemperie" });
      qs.push({ id: "cristales", texto: "¿Tiene cristales?", tipo: "boolean", ayuda: "Menos superficie a pintar, pero necesita cinta de protección" });
      qs.push({ id: "cantidad", texto: "¿Cuántas puertas iguales?", tipo: "number", unidad: "ud", placeholder: "Ej: 1" });
    }
    if (r.elemento === "ventana") {
      qs.push({ id: "color_actual", texto: "¿De qué color es ahora?", tipo: "choice", opciones: [{ label: "Claro", value: "claro" }, { label: "Oscuro", value: "oscuro" }] });
      qs.push({ id: "color_nuevo", texto: "¿A qué color la querés pintar?", tipo: "choice", opciones: [{ label: "Claro", value: "claro" }, { label: "Oscuro", value: "oscuro" }] });
      qs.push({ id: "tamano_especial", texto: "¿Es tamaño estándar (1.20 x 1.20m) o especial?", tipo: "choice", opciones: [
        { label: "Estándar", value: "estandar" }, { label: "Especial, la mido yo", value: "especial" },
      ]});
      if (r.tamano_especial === "especial") qs.push({ id: "medidas", texto: "Medidas reales de la ventana", tipo: "ancho_alto" });
      qs.push({ id: "exterior", texto: "¿Da al exterior?", tipo: "boolean" });
      qs.push({ id: "cristales", texto: "¿Tiene cristales?", tipo: "boolean" });
      qs.push({ id: "cantidad", texto: "¿Cuántas ventanas iguales?", tipo: "number", unidad: "ud", placeholder: "Ej: 1" });
    }
    if (r.elemento === "radiador") {
      qs.push({ id: "nivel_oxido", texto: "¿Tiene óxido?", tipo: "choice", opciones: [
        { label: "Nada o casi nada", value: "ninguno" }, { label: "Un poco", value: "poco" }, { label: "Bastante", value: "mucho" },
      ]});
      qs.push({ id: "cantidad", texto: "¿Cuántos radiadores?", tipo: "number", unidad: "ud", placeholder: "Ej: 1" });
    }
    if (r.elemento === "reja") {
      qs.push({ id: "nivel_oxido", texto: "¿Tiene óxido?", tipo: "choice", opciones: [
        { label: "Nada o casi nada", value: "ninguno" }, { label: "Un poco", value: "poco" }, { label: "Bastante, en toda la superficie", value: "mucho" },
      ]});
      qs.push({ id: "exterior", texto: "¿Está en el exterior?", tipo: "boolean" });
      qs.push({ id: "ml", texto: "¿Cuántos metros lineales, aproximadamente?", tipo: "number", unidad: "ml", placeholder: "Ej: 6" });
    }
    if (r.elemento === "mueble") {
      qs.push({ id: "acabado", texto: "¿Pintar de color o barnizar transparente?", tipo: "choice", opciones: [
        { label: "Pintar de color (opaco)", value: "pintura" }, { label: "Barnizar transparente", value: "barniz" },
      ]});
      qs.push({ id: "sabe_medidas", texto: "¿Sabés las medidas del mueble?", tipo: "boolean" });
      if (r.sabe_medidas) qs.push({ id: "medidas_mueble", texto: "Alto x Ancho x Profundo del mueble", tipo: "ancho_alto_profundo" });
      else qs.push({ id: "m2_mueble", texto: "¿Cuántos m² aproximados de superficie a pintar?", tipo: "number", unidad: "m²", placeholder: "Ej: 4" });
      qs.push({ id: "cantidad", texto: "¿Cuántos muebles iguales?", tipo: "number", unidad: "ud", placeholder: "Ej: 1" });
    }
    if (r.elemento === "pasamanos") qs.push({ id: "ml", texto: "¿Cuántos metros lineales de pasamanos?", tipo: "number", unidad: "ml", placeholder: "Ej: 4" });
    if (r.elemento === "rodapie") qs.push({ id: "ml", texto: "¿Cuántos metros lineales de rodapié?", tipo: "number", unidad: "ml", placeholder: "Ej: 20" });
    return qs;
  },
  compilar: (r, trabajos) => {
    const lineas = [];
    const contraste = r.color_actual === "oscuro" && r.color_nuevo === "claro";
    const medidas = r.tamano_especial === "especial" && r.medidas ? { ancho_m: r.medidas.ancho, alto_m: r.medidas.alto } : {};
    if (r.elemento === "puerta") {
      const base = { exterior: !!r.exterior, con_cristales: !!r.cristales, contraste_alto: contraste, ...medidas };
      if (r.material === "madera") {
        if (r.que_hacer === "decapar") {
          lineas.push(linea(trabajos, "Decapado puerta", r.cantidad, base));
          lineas.push(linea(trabajos, r.molduras ? "Lacado puerta con molduras" : "Lacado puerta lisa 2 caras", r.cantidad, base));
        } else if (r.que_hacer === "lacar") {
          lineas.push(linea(trabajos, r.molduras ? "Lacado puerta con molduras" : "Lacado puerta lisa 2 caras", r.cantidad, base));
        } else if (r.que_hacer === "barnizar") {
          lineas.push(linea(trabajos, "Barnizado puerta madera", r.cantidad, { exterior: !!r.exterior, con_cristales: !!r.cristales, ...medidas }));
        }
      } else if (r.material === "metal") {
        lineas.push(linea(trabajos, "Pintura puerta metálica/hierro", r.cantidad, { ...base, nivel_oxido: r.nivel_oxido || "ninguno" }));
      } else if (r.material === "aluminio") {
        lineas.push(linea(trabajos, "Pintura puerta aluminio", r.cantidad, base));
      }
    }
    if (r.elemento === "ventana") {
      lineas.push(linea(trabajos, "Esmalte ventana madera", r.cantidad, { exterior: !!r.exterior, con_cristales: !!r.cristales, contraste_alto: contraste, ...medidas }));
    }
    if (r.elemento === "radiador") {
      lineas.push(linea(trabajos, "Esmalte radiador", r.cantidad, { nivel_oxido: r.nivel_oxido || "ninguno" }));
    }
    if (r.elemento === "reja") {
      lineas.push(linea(trabajos, "Esmalte reja/barandilla", r.ml, { exterior: !!r.exterior, nivel_oxido: r.nivel_oxido || "ninguno" }));
    }
    if (r.elemento === "mueble") {
      const nombre = r.acabado === "barniz" ? "Barnizado de mueble de madera" : "Pintura de mueble (armario, cómoda)";
      let m2 = Number(r.m2_mueble) || 0;
      if (r.sabe_medidas && r.medidas_mueble) {
        const { ancho, alto, profundo } = r.medidas_mueble;
        if (ancho > 0 && alto > 0 && profundo > 0) m2 = ancho * alto + 2 * profundo * alto + ancho * profundo;
      }
      lineas.push(linea(trabajos, nombre, m2 * (Number(r.cantidad) || 1)));
    }
    if (r.elemento === "pasamanos") lineas.push(linea(trabajos, "Pasamanos de escalera (madera)", r.ml));
    if (r.elemento === "rodapie") lineas.push(linea(trabajos, "Rodapié existente (repintar)", r.ml));
    return { lineas: lineas.filter(Boolean), respuestas_diagnostico: {} };
  },
};

// ---------- CERÁMICA ----------
const ceramicaFlujo = {
  getPreguntas: (r) => {
    const qs = [
      { id: "tipo_trabajo", texto: "¿Qué necesitás?", tipo: "choice", opciones: [
        { label: "Colocar cerámica nueva", value: "nueva" },
        { label: "Solo rejuntar (la cerámica ya está, hay que renovar las juntas)", value: "rejuntado" },
      ]},
    ];
    if (r.tipo_trabajo === "rejuntado") {
      qs.push({ id: "m2", texto: "¿Cuántos m² tiene la zona a rejuntar?", tipo: "number", unidad: "m²", placeholder: "Ej: 8" });
      return qs;
    }
    qs.push({ id: "donde", texto: "¿Dónde va la cerámica?", tipo: "choice", opciones: [
      { label: "Solo pared (alicatado)", value: "pared" }, { label: "Solo suelo (solado)", value: "suelo" }, { label: "Pared + suelo", value: "ambos" },
    ]});
    if (r.donde === "pared" || r.donde === "ambos") {
      qs.push({ id: "formato_pared", texto: "¿Qué tipo de azulejo de pared?", tipo: "choice", opciones: [
        { label: "Estándar", value: "estandar" }, { label: "Formato grande", value: "grande" },
      ]});
      qs.push({ id: "pladur", texto: "¿La pared es de pladur/tabique seco?", tipo: "boolean", ayuda: "Necesita un adhesivo distinto al normal" });
      qs.push({ id: "m2_pared", texto: "¿Cuántos m² de pared?", tipo: "number", unidad: "m²", placeholder: "Ej: 15" });
      qs.push({ id: "retirada_pared", texto: "¿Hay azulejo actual que retirar de esa pared?", tipo: "boolean" });
    }
    if (r.donde === "suelo" || r.donde === "ambos") {
      qs.push({ id: "formato_suelo", texto: "¿Qué tipo de gres para el suelo?", tipo: "choice", opciones: [
        { label: "Estándar", value: "estandar" }, { label: "Porcelánico gran formato", value: "grande" },
      ]});
      qs.push({ id: "exterior_suelo", texto: "¿Es una terraza/patio exterior?", tipo: "boolean", ayuda: "Necesita gres antideslizante" });
      qs.push({ id: "m2_suelo", texto: "¿Cuántos m² de suelo?", tipo: "number", unidad: "m²", placeholder: "Ej: 10" });
      qs.push({ id: "desnivel", texto: "¿El suelo base tiene desnivel?", tipo: "choice", opciones: [
        { label: "No, está nivelado", value: "no" }, { label: "Leve (1-3mm)", value: "leve" }, { label: "Importante (3-10mm)", value: "grave" },
      ]});
      qs.push({ id: "retirada_suelo", texto: "¿Hay suelo actual que retirar?", tipo: "boolean" });
    }
    qs.push({ id: "sanitarios", texto: "¿Hay plato de ducha, bañera o sanitario que sellar contra el azulejo?", tipo: "boolean" });
    return qs;
  },
  compilar: (r, trabajos) => {
    const lineas = [];
    if (r.tipo_trabajo === "rejuntado") {
      lineas.push(linea(trabajos, "Rejuntado", r.m2));
      return { lineas: lineas.filter(Boolean), respuestas_diagnostico: {} };
    }
    if (r.donde === "pared" || r.donde === "ambos") {
      if (r.retirada_pared) lineas.push(linea(trabajos, "Retirada alicatado", r.m2_pared));
      const nombre = r.formato_pared === "grande" ? "Alicatado formato grande" : "Alicatado pared estándar";
      lineas.push(linea(trabajos, nombre, r.m2_pared, { pladur: !!r.pladur, con_sanitarios: !!r.sanitarios }));
    }
    if (r.donde === "suelo" || r.donde === "ambos") {
      if (r.retirada_suelo) lineas.push(linea(trabajos, "Retirada solado", r.m2_suelo));
      if (r.desnivel === "leve") lineas.push(linea(trabajos, "Nivelado autonivelante fino 1-3mm", r.m2_suelo));
      if (r.desnivel === "grave") lineas.push(linea(trabajos, "Nivelado autonivelante estándar 3-10mm", r.m2_suelo));
      const nombre = r.formato_suelo === "grande" ? "Solado porcelánico grande" : "Solado gres estándar";
      lineas.push(linea(trabajos, nombre, r.m2_suelo, { exterior: !!r.exterior_suelo, con_sanitarios: !!r.sanitarios }));
    }
    return { lineas: lineas.filter(Boolean), respuestas_diagnostico: {} };
  },
};

// ---------- MAMPARAS ----------
const mamparasFlujo = {
  getPreguntas: (r) => {
    const qs = [
      { id: "tipo", texto: "¿Qué tipo de mampara?", tipo: "choice", opciones: [
        { label: "Frontal", value: "Instalación mampara frontal" },
        { label: "Angular (esquina)", value: "Instalación mampara angular" },
        { label: "Para bañera", value: "Instalación mampara bañera" },
        { label: "Semicircular/curva", value: "Instalación mampara semicircular" },
      ]},
      { id: "tamano_especial", texto: "¿Medida estándar o especial?", tipo: "choice", opciones: [
        { label: "Estándar", value: "estandar" }, { label: "Especial, la mido yo", value: "especial" },
      ]},
    ];
    if (r.tamano_especial === "especial") qs.push({ id: "medidas", texto: "Ancho x Alto real de la mampara", tipo: "ancho_alto" });
    qs.push({ id: "retirada", texto: "¿Hay que retirar una mampara antigua?", tipo: "boolean" });
    qs.push({ id: "grifo", texto: "¿Aprovechamos y cambiamos también el grifo de la ducha?", tipo: "boolean" });
    qs.push({ id: "cantidad", texto: "¿Cuántas mamparas iguales?", tipo: "number", unidad: "ud", placeholder: "Ej: 1" });
    return qs;
  },
  compilar: (r, trabajos) => {
    const lineas = [];
    const medidas = r.tamano_especial === "especial" && r.medidas ? { ancho_m: r.medidas.ancho, alto_m: r.medidas.alto } : {};
    if (r.retirada) lineas.push(linea(trabajos, "Retirada mampara antigua", r.cantidad || 1));
    if (r.tipo) lineas.push(linea(trabajos, r.tipo, r.cantidad || 1, medidas));
    if (r.grifo) lineas.push(linea(trabajos, "Cambio grifo ducha/bañera", 1));
    return { lineas: lineas.filter(Boolean), respuestas_diagnostico: {} };
  },
};

// ---------- COCINAS ----------
const cocinasFlujo = {
  getPreguntas: (r) => [
    { id: "bajos", texto: "¿Cuántos módulos bajos con puertas?", tipo: "number", unidad: "ud", placeholder: "Ej: 4" },
    { id: "bajos_cajones", texto: "¿Cuántos módulos bajos con cajones?", tipo: "number", unidad: "ud", placeholder: "Ej: 2" },
    { id: "altos", texto: "¿Cuántos módulos altos?", tipo: "number", unidad: "ud", placeholder: "Ej: 4" },
    { id: "columna", texto: "¿Cuántos módulos columna (para horno/nevera integrada)?", tipo: "number", unidad: "ud", placeholder: "Ej: 0" },
    { id: "tipo_encimera", texto: "¿Qué encimera?", tipo: "choice", opciones: [
      { label: "Laminada", value: "laminada" }, { label: "Granito/cuarzo", value: "granito" }, { label: "Ya la tengo, no hace falta", value: "no" },
    ]},
    { id: "ml_encimera", texto: "¿Cuántos metros lineales de encimera?", tipo: "number", unidad: "ml", placeholder: "Ej: 4", condicion: (r2) => r2.tipo_encimera !== "no" },
    { id: "fregadero", texto: "¿Necesitás fregadero nuevo?", tipo: "boolean" },
    { id: "grifo", texto: "¿Y grifo de cocina nuevo?", tipo: "boolean" },
    { id: "horno", texto: "¿Instalamos horno?", tipo: "boolean" },
    { id: "placa", texto: "¿Instalamos placa (vitro/inducción)?", tipo: "boolean" },
    { id: "campana", texto: "¿Instalamos campana extractora?", tipo: "boolean" },
    { id: "zocalo", texto: "¿Cuántos metros lineales de zócalo?", tipo: "number", unidad: "ml", placeholder: "Ej: 6" },
  ].filter((q) => !q.condicion || q.condicion(r || {})),
  compilar: (r, trabajos) => {
    const lineas = [];
    if (r.bajos) lineas.push(linea(trabajos, "Montaje mueble bajo (puertas)", r.bajos));
    if (r.bajos_cajones) lineas.push(linea(trabajos, "Montaje mueble bajo (cajones)", r.bajos_cajones, { cantidad_cajones: r.bajos_cajones * 3 }));
    if (r.altos) lineas.push(linea(trabajos, "Montaje mueble alto (puertas)", r.altos));
    if (r.columna) lineas.push(linea(trabajos, "Montaje mueble columna", r.columna, { cantidad_puertas: r.columna * 2 }));
    if (r.tipo_encimera === "laminada" && r.ml_encimera) lineas.push(linea(trabajos, "Instalación encimera laminada", r.ml_encimera));
    if (r.tipo_encimera === "granito" && r.ml_encimera) lineas.push(linea(trabajos, "Instalación encimera granito/cuarzo", r.ml_encimera));
    if (r.fregadero) lineas.push(linea(trabajos, "Instalación fregadero", 1));
    if (r.grifo) lineas.push(linea(trabajos, "Instalación grifo cocina", 1));
    if (r.horno) lineas.push(linea(trabajos, "Instalación horno", 1));
    if (r.placa) lineas.push(linea(trabajos, "Instalación placa vitro/inducción", 1));
    if (r.campana) lineas.push(linea(trabajos, "Instalación campana extractora", 1));
    if (r.zocalo) lineas.push(linea(trabajos, "Zócalo de cocina", r.zocalo));
    return { lineas: lineas.filter(Boolean), respuestas_diagnostico: {} };
  },
};

// ---------- Módulos tipo checklist (fontanería, electricidad, cierres, instalaciones, mantenimiento) ----------
// Estos no son un relato lineal (una reforma con pasos), son un catálogo de
// necesidades independientes: el cliente marca lo que necesita y pone
// cantidad. Es el enfoque correcto para estos, no forzar un cuestionario narrativo.
const checklistFlujo = (items) => ({
  getPreguntas: () => [{ id: "seleccion", texto: "¿Qué necesitás?", tipo: "checklist", opciones: items }],
  compilar: (r, trabajos) => {
    const lineas = (r.seleccion || []).map((sel) => linea(trabajos, sel.nombre, sel.cantidad || 1, sel.extra || {}));
    return { lineas: lineas.filter(Boolean), respuestas_diagnostico: {} };
  },
});

const fontaneriaFlujo = checklistFlujo([
  { nombre: "Cambio WC completo", label: "Cambiar WC/inodoro" },
  { nombre: "Cambio lavabo con pedestal", label: "Cambiar lavabo" },
  { nombre: "Instalación de bidé", label: "Instalar bidé" },
  { nombre: "Cambio plato ducha", label: "Cambiar plato de ducha" },
  { nombre: "Instalación columna ducha", label: "Instalar columna de ducha" },
  { nombre: "Cambio grifo lavabo/bidé", label: "Cambiar grifo de lavabo" },
  { nombre: "Cambio grifo ducha/bañera", label: "Cambiar grifo de ducha" },
  { nombre: "Cambio sifón", label: "Cambiar sifón" },
  { nombre: "Cambio válvula de desagüe", label: "Cambiar válvula de desagüe" },
  { nombre: "Toma de agua lavadora/lavavajillas", label: "Toma de agua para lavadora/lavavajillas" },
  { nombre: "Cambio de llave de paso", label: "Cambiar llave de paso" },
  { nombre: "Punto de agua nuevo", label: "Punto de agua nuevo (reubicar/agregar)" },
]);

const electricidadFlujo = checklistFlujo([
  { nombre: "Punto de luz nuevo", label: "Punto de luz nuevo" },
  { nombre: "Instalación lámpara/plafón", label: "Lámpara/plafón sobre punto existente" },
  { nombre: "Instalación foco empotrable/downlight", label: "Foco empotrable/downlight" },
  { nombre: "Punto de enchufe nuevo", label: "Enchufe nuevo" },
  { nombre: "Punto de interruptor nuevo", label: "Interruptor nuevo" },
  { nombre: "Línea eléctrica nueva", label: "Línea eléctrica nueva (metros)" },
  { nombre: "Cuadro eléctrico básico", label: "Cuadro eléctrico" },
  { nombre: "Instalación termo eléctrico", label: "Termo eléctrico" },
  { nombre: "Instalación extractor de baño", label: "Extractor de baño" },
  { nombre: "Cambio toma antena/teléfono", label: "Toma de antena/teléfono" },
]);

const instalacionesFlujo = checklistFlujo([
  { nombre: "Instalación ventana PVC 1 hoja", label: "Ventana PVC 1 hoja" },
  { nombre: "Instalación ventana PVC 2 hojas", label: "Ventana PVC 2 hojas" },
  { nombre: "Instalación ventana aluminio", label: "Ventana aluminio" },
  { nombre: "Instalación marco ventana", label: "Marco de ventana" },
  { nombre: "Instalación persiana PVC", label: "Persiana PVC" },
  { nombre: "Instalación persiana aluminio", label: "Persiana aluminio" },
  { nombre: "Motorización persiana", label: "Motorizar persiana" },
  { nombre: "Instalación puerta interior completa", label: "Puerta interior" },
  { nombre: "Instalación puerta corredera", label: "Puerta corredera" },
  { nombre: "Instalación puerta blindada", label: "Puerta blindada" },
  { nombre: "Instalación marco puerta", label: "Marco de puerta" },
  { nombre: "Instalación mosquitera fija", label: "Mosquitera fija" },
  { nombre: "Instalación mosquitera corredera", label: "Mosquitera corredera" },
  { nombre: "Cambio de cristal roto", label: "Cambio de cristal roto (m²)" },
  { nombre: "Instalación reja de ventana", label: "Reja de ventana nueva (m²)" },
  { nombre: "Instalación puerta de garaje", label: "Puerta de garaje" },
  { nombre: "Motorización puerta de garaje", label: "Motorizar puerta de garaje" },
]);

const cierresFlujo = checklistFlujo([
  { nombre: "Cambio cerradura estándar", label: "Cambiar cerradura estándar" },
  { nombre: "Cambio cerradura seguridad", label: "Cambiar cerradura de seguridad (multipunto)" },
  { nombre: "Cambio bombín", label: "Cambiar bombín" },
  { nombre: "Instalación cerrojo", label: "Instalar cerrojo" },
  { nombre: "Instalación mirilla", label: "Instalar mirilla" },
  { nombre: "Instalación escudo protector de bombín", label: "Escudo protector de bombín" },
  { nombre: "Instalación cerradura electrónica", label: "Cerradura electrónica/inteligente" },
  { nombre: "Ajuste puerta", label: "Ajustar puerta que roza/no cierra bien" },
  { nombre: "Apertura puerta urgente", label: "Apertura urgente (me quedé sin llaves)" },
]);

const mantenimientoFlujo = checklistFlujo([
  { nombre: "Reparación cisterna", label: "Reparar cisterna que pierde agua" },
  { nombre: "Desatasco tubería", label: "Desatascar tubería" },
  { nombre: "Cambio grifo lavabo/bidé", label: "Cambiar grifo de lavabo/bidé" },
  { nombre: "Cambio grifo ducha/bañera", label: "Cambiar grifo de ducha/bañera" },
  { nombre: "Cambio sifón", label: "Cambiar sifón" },
  { nombre: "Cambio válvula de desagüe", label: "Cambiar válvula de desagüe" },
  { nombre: "Toma de agua lavadora/lavavajillas", label: "Toma de agua para lavadora/lavavajillas" },
  { nombre: "Cambio de llave de paso", label: "Cambiar llave de paso" },
  { nombre: "Instalación lámpara/plafón", label: "Instalar lámpara/plafón" },
  { nombre: "Cambio toma antena/teléfono", label: "Cambiar toma de antena/teléfono" },
]);

const FLUJOS = {
  suelos: suelosFlujo,
  pintura_paramentos: paramentosFlujo,
  pintura_carpinteria: carpinteriaFlujo,
  ceramica: ceramicaFlujo,
  mamparas: mamparasFlujo,
  cocinas: cocinasFlujo,
  fontaneria: fontaneriaFlujo,
  electricidad: electricidadFlujo,
  instalaciones: instalacionesFlujo,
  cierres: cierresFlujo,
  mantenimiento: mantenimientoFlujo,
};

// ==========================================
// UI
// ==========================================

const OpcionCard = ({ activo, onClick, children }) => (
  <div
    onClick={onClick}
    className={`card p-3 mb-2 ${activo ? "factor-card-selected" : ""}`}
    style={{ cursor: "pointer", border: activo ? "2px solid var(--brand-accent)" : "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}
  >
    <div className={`factor-checkbox ${activo ? "factor-checkbox-checked" : ""}`}>
      {activo && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
    </div>
    <div className="text-small font-bold">{children}</div>
  </div>
);

const PreguntaRenderer = ({ pregunta, valor, onChange }) => {
  if (pregunta.tipo === "choice") {
    return (
      <div>
        {pregunta.opciones.map((op) => (
          <OpcionCard key={op.value} activo={valor === op.value} onClick={() => onChange(op.value)}>{op.label}</OpcionCard>
        ))}
      </div>
    );
  }
  if (pregunta.tipo === "boolean") {
    return (
      <div className="flex gap-3">
        <button onClick={() => onChange(true)} className={`btn ${valor === true ? "btn-accent" : "btn-outline"}`} style={{ flex: 1 }}>Sí</button>
        <button onClick={() => onChange(false)} className={`btn ${valor === false ? "btn-accent" : "btn-outline"}`} style={{ flex: 1 }}>No</button>
      </div>
    );
  }
  if (pregunta.tipo === "number") {
    return (
      <Input type="number" min="0" step="0.5" placeholder={pregunta.placeholder} value={valor ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
    );
  }
  if (pregunta.tipo === "ancho_alto") {
    const v = valor || {};
    return (
      <div className="flex gap-2">
        <div style={{ flex: 1 }}>
          <label className="input-label">Ancho (m)</label>
          <Input type="number" min="0" step="0.05" value={v.ancho ?? ""} onChange={(e) => onChange({ ...v, ancho: Number(e.target.value) })} />
        </div>
        <div style={{ flex: 1 }}>
          <label className="input-label">Alto (m)</label>
          <Input type="number" min="0" step="0.05" value={v.alto ?? ""} onChange={(e) => onChange({ ...v, alto: Number(e.target.value) })} />
        </div>
      </div>
    );
  }
  if (pregunta.tipo === "ancho_alto_profundo") {
    const v = valor || {};
    return (
      <div className="flex gap-2">
        {["ancho", "alto", "profundo"].map((campo) => (
          <div key={campo} style={{ flex: 1 }}>
            <label className="input-label" style={{ textTransform: "capitalize" }}>{campo} (m)</label>
            <Input type="number" min="0" step="0.05" value={v[campo] ?? ""} onChange={(e) => onChange({ ...v, [campo]: Number(e.target.value) })} />
          </div>
        ))}
      </div>
    );
  }
  if (pregunta.tipo === "checklist") {
    const seleccion = valor || [];
    const toggle = (item) => {
      const existe = seleccion.find((s) => s.nombre === item.nombre);
      if (existe) onChange(seleccion.filter((s) => s.nombre !== item.nombre));
      else onChange([...seleccion, { nombre: item.nombre, cantidad: 1 }]);
    };
    const setCantidad = (item, cant) => {
      onChange(seleccion.map((s) => (s.nombre === item.nombre ? { ...s, cantidad: cant } : s)));
    };
    return (
      <div>
        {pregunta.opciones.map((item) => {
          const sel = seleccion.find((s) => s.nombre === item.nombre);
          return (
            <div key={item.nombre} className="card p-3 mb-2" style={{ border: sel ? "2px solid var(--brand-accent)" : "1px solid var(--border)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => toggle(item)}>
                <div className={`factor-checkbox ${sel ? "factor-checkbox-checked" : ""}`}>
                  {sel && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
                </div>
                <div className="text-small font-bold" style={{ flex: 1 }}>{item.label}</div>
              </div>
              {sel && (
                <div style={{ marginTop: 8, marginLeft: 30 }}>
                  <Input type="number" min="0.5" step="0.5" value={sel.cantidad} onChange={(e) => setCantidad(item, Number(e.target.value))} style={{ maxWidth: 120 }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

export const DiagnosticoInteligente = ({ trabajos, modulos, onGenerarPresupuesto, onNavigate }) => {
  // Foto real de marca por módulo (misma fuente que la portada y el paso 2
  // del presupuesto) — si un módulo no tiene foto todavía, se usa su emoji.
  const fotoPorModulo = Object.fromEntries((modulos || []).map((m) => [m.id, m.foto_icono]));
  const [modulo, setModulo] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [indice, setIndice] = useState(0);
  const [paso, setPaso] = useState("modulo"); // modulo | preguntas | resumen | contacto

  const flujo = modulo ? FLUJOS[modulo] : null;
  const preguntas = useMemo(() => (flujo ? flujo.getPreguntas(respuestas) : []), [flujo, respuestas]);
  const preguntaActual = preguntas[indice];

  const elegirModulo = (id) => {
    setModulo(id);
    setRespuestas({});
    setIndice(0);
    setPaso("preguntas");
  };

  const responder = (valor) => {
    setRespuestas((prev) => ({ ...prev, [preguntaActual.id]: valor }));
  };

  const siguiente = () => {
    if (indice + 1 < preguntas.length) {
      setIndice(indice + 1);
    } else {
      setPaso("resumen");
    }
  };

  const anterior = () => {
    if (indice > 0) setIndice(indice - 1);
    else setPaso("modulo");
  };

  const respuestaValida = () => {
    if (!preguntaActual) return false;
    const v = respuestas[preguntaActual.id];
    if (preguntaActual.tipo === "boolean") return v !== undefined;
    if (preguntaActual.tipo === "number") return v !== "" && v !== undefined && v > 0;
    if (preguntaActual.tipo === "choice") return !!v;
    if (preguntaActual.tipo === "ancho_alto") return v && v.ancho > 0 && v.alto > 0;
    if (preguntaActual.tipo === "ancho_alto_profundo") return v && v.ancho > 0 && v.alto > 0 && v.profundo > 0;
    if (preguntaActual.tipo === "checklist") return v && v.length > 0;
    return true;
  };

  const compilado = useMemo(() => {
    if (!flujo || paso !== "resumen") return { lineas: [] };
    return flujo.compilar(respuestas, trabajos);
  }, [flujo, respuestas, paso, trabajos]);

  // ---- PASO 1: elegir módulo ----
  if (paso === "modulo") {
    return (
      <div className="pb-24" data-testid="diagnostico-screen">
        <div className="hero-header" style={{ padding: "24px 20px" }}>
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="flex items-center gap-2 mb-2">
              <MagnifyingGlass size={24} weight="bold" style={{ color: "var(--brand-accent)" }} />
            </div>
            <div className="text-h2 font-display">Diagnóstico Inteligente</div>
            <div className="text-small" style={{ opacity: 0.8, marginTop: 4 }}>
              Elegí qué necesitás y te vamos a hacer las preguntas justas para armar tu presupuesto
            </div>
          </div>
        </div>
        <div className="container" style={{ marginTop: -20, position: "relative", zIndex: 2 }}>
          <div className="card p-4">
            <div className="grid grid-cols-2 gap-2">
              {MODULOS_DIAG.map((m) => (
                <button key={m.id} onClick={() => elegirModulo(m.id)} className="card p-3 text-left" style={{ border: "1px solid var(--border)" }}>
                  {fotoPorModulo[m.id] ? (
                    <img src={fotoPorModulo[m.id]} alt="" className="list-item-photo" style={{ marginBottom: 4 }} />
                  ) : (
                    <div style={{ fontSize: "1.5rem", marginBottom: 4 }}>{m.emoji}</div>
                  )}
                  <div className="font-bold text-tiny">{m.nombre}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- PASO 2: preguntas, una por pantalla ----
  if (paso === "preguntas" && preguntaActual) {
    const moduloInfo = MODULOS_DIAG.find((m) => m.id === modulo);
    return (
      <div className="pb-24">
        <div className="container pt-4">
          <div className="flex items-center gap-2 mb-3">
            {fotoPorModulo[moduloInfo?.id] ? (
              <img src={fotoPorModulo[moduloInfo.id]} alt="" className="chip-photo" />
            ) : (
              <span style={{ fontSize: "1.3rem" }}>{moduloInfo?.emoji}</span>
            )}
            <span className="text-tiny font-bold text-secondary">{moduloInfo?.nombre}</span>
          </div>
          <div className="progress-bar mb-6">
            {preguntas.map((_, i) => (
              <div key={i} className={`progress-step ${indice >= i ? "progress-step-active" : ""}`} />
            ))}
          </div>
          <div className="card p-4 mb-4">
            <div className="text-h3 font-display mb-1">{preguntaActual.texto}</div>
            {preguntaActual.ayuda && <div className="text-tiny text-secondary mb-3">{preguntaActual.ayuda}</div>}
            {preguntaActual.hint && <div className="text-tiny text-secondary mb-3">{preguntaActual.hint}</div>}
            <div className="mt-3">
              <PreguntaRenderer pregunta={preguntaActual} valor={respuestas[preguntaActual.id]} onChange={responder} />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={anterior} className="flex-1"><CaretLeft size={18} /> Atrás</Button>
            <Button variant="accent" onClick={siguiente} disabled={!respuestaValida()} className="flex-1">
              {indice + 1 < preguntas.length ? "Siguiente" : "Ver resumen"} <CaretRight size={18} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- PASO 3: resumen + confirmar generar presupuesto ----
  if (paso === "resumen") {
    return (
      <div className="pb-24">
        <div className="container pt-4">
          <div className="text-h2 font-display mb-1">Tu diagnóstico está listo ✅</div>
          <div className="text-small text-secondary mb-4">Esto es lo que armamos con tus respuestas — se puede editar todo en el siguiente paso.</div>
          <div className="card p-4 mb-4">
            {compilado.lineas.length === 0 && <div className="text-small text-secondary">No se pudo armar ningún trabajo con esas respuestas. Volvé atrás y revisá.</div>}
            {compilado.lineas.map((l, i) => (
              <div key={i} className="flex justify-between items-center py-2" style={{ borderBottom: i < compilado.lineas.length - 1 ? "1px solid var(--border)" : "none" }}>
                <div className="text-small font-bold">{l.trabajo_nombre}</div>
                <div className="text-tiny text-secondary">{l.cantidad} {l.unidad}</div>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            <Button
              variant="accent"
              className="btn-full"
              disabled={compilado.lineas.length === 0}
              onClick={() => onGenerarPresupuesto(compilado)}
            >
              ¿Armamos el presupuesto con esto? <ArrowRight size={18} />
            </Button>
            <Button variant="outline" className="btn-full" onClick={() => { setIndice(0); setPaso("preguntas"); }}>
              <CaretLeft size={18} /> Volver a responder
            </Button>
            <Button variant="ghost" className="btn-full" onClick={() => { setModulo(null); setRespuestas({}); setPaso("modulo"); }}>
              Empezar otro diagnóstico
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default DiagnosticoInteligente;
